/**
 * LoveLangHeader
 *
 * The "Love Language FM" wordmark with an intimate HeartbeatBar below.
 * BPM state is now read from the shared HeartbeatContext — a single mic
 * session drives all instances (nav, hero, footer) in unison.
 *
 * Usage variants:
 *   <LoveLangHeader />               — nav size (small)
 *   <LoveLangHeader size="hero" />   — hero h1 size
 *   <LoveLangHeader size="footer" /> — footer size
 */

import { useHeartbeat } from "@/contexts/HeartbeatContext";
import HeartbeatBar from "./HeartbeatBar";

type Size = "nav" | "hero" | "footer";

interface LoveLangHeaderProps {
  size?: Size;
  className?: string;
}

const SIZE_CONFIG: Record<Size, {
  wrapper: string;
  text: string;
  barW: number;
  barH: number;
  rowGap: string;   // gap between bar and slogan on the same row
  slogan: string;
  sloganTrack: string; // base letter-spacing fed into the drift keyframe
}> = {
  nav: {
    wrapper: "inline-flex flex-col items-start gap-[4px]",
    text: "font-serif text-sm font-semibold tracking-tight",
    barW: 22,
    barH: 5,
    rowGap: "gap-[5px]",
    slogan: "font-serif text-[8px] font-light text-muted-foreground italic leading-none",
    sloganTrack: "0.22em",
  },
  hero: {
    wrapper: "inline-flex flex-col items-start gap-[6px]",
    text: "t-display font-semibold tracking-tight",
    barW: 44,
    barH: 9,
    rowGap: "gap-[8px]",
    slogan: "font-serif text-sm font-light text-muted-foreground/65 italic leading-none",
    sloganTrack: "0.25em",
  },
  footer: {
    wrapper: "inline-flex flex-col items-start gap-[4px]",
    text: "font-serif text-base font-semibold",
    barW: 26,
    barH: 6,
    rowGap: "gap-[6px]",
    slogan: "font-serif text-[9px] font-light text-muted-foreground/70 italic leading-none",
    sloganTrack: "0.22em",
  },
};

export default function LoveLangHeader({ size = "nav", className = "" }: LoveLangHeaderProps) {
  const { bpm, isListening, isHeartbeat } = useHeartbeat();
  const cfg = SIZE_CONFIG[size];

  // Hero colours mirror the existing hardcoded style
  const textColor = size === "hero" ? { color: "#999999" } : undefined;
  const fmColor   = size === "hero" ? { color: "#FFFFFF" } : undefined;

  return (
    <span className={`${cfg.wrapper} ${className}`}>
      {/* Wordmark */}
      <span className={cfg.text} style={textColor}>
        Love Language
        <span
          className={size === "footer" ? "text-absa-red" : undefined}
          style={size !== "footer" ? fmColor : undefined}
        > FM</span>
      </span>

      {/* Sound bar + slogan — co-planar, vertically centred */}
      <span className={`flex items-center ${cfg.rowGap}`}>
        {/* Bar leads left — fades in first */}
        <span
          className="flex-shrink-0 flex items-center"
          style={{ opacity: 0, animation: "slogan-appear 0.35s ease-out 0.05s forwards" }}
        >
          <HeartbeatBar
            bpm={bpm}
            isHeartbeat={isHeartbeat}
            isListening={isListening}
            width={cfg.barW}
            height={cfg.barH}
          />
        </span>

        {/* Slogan — whisper-like, smoke entrance then ongoing drift + letter-spacing breath */}
        <span
          className={cfg.slogan}
          aria-hidden="true"
          style={{
            opacity: 0,
            letterSpacing: cfg.sloganTrack,
            ["--slogan-track" as string]: cfg.sloganTrack,
            animation: [
              "slogan-appear 0.6s ease-out 0.45s forwards",
              "slogan-drift 6s cubic-bezier(0.45, 0, 0.55, 1) 1.2s infinite",
            ].join(", "),
          }}
        >
          Sofa silahlane FM
        </span>

        {/* Mic-active dot — only when listening */}
        {isListening && (
          <span
            className="flex-shrink-0 w-1 h-1 rounded-full bg-primary animate-pulse"
            title="Heartbeat sync active"
          />
        )}
      </span>
    </span>
  );
}
